// active-pathway.js — which of the 8 study paths is the learner currently in?
//
// Brief 157. iOS canon (KFEChangeStudyPathViewController.m, read 2026-06-18):
// the active pathway is persisted as the plist name string (e.g.
// "kfe_zero_to_fluency") in the `active_pathway` column of
// `app_progress_variables`, and switching pathways NEVER resets progress —
// learner completion is global, keyed by item_id, not scoped per pathway
// (KFEDatabaseManager queries `app_item_info` on item_id alone). The web mirror
// keeps the same value under the same logical key.
//
// Two stores, one writer:
//   • localStorage `kfe_pathway_id` (Brief 105 `kfe_*` convention) is the
//     CANONICAL fast/synchronous source — the switcher screen and the Settings
//     row read it without an async hop, and it survives a reload.
//   • The learner-store `active_pathway` setting (IndexedDB) is a MIRROR written
//     by the same setter, so the existing async readers that already resolve the
//     active pathway that way — kfe-pathway, kfe-lesson-detail, kfe-exercise-host,
//     kfe-audio/video-exercise, kfe-lesson-report, and learner-store's own
//     lesson-completion funnel — keep working untouched. This setter is the ONLY
//     writer of either store, so they can't drift.
//
// On a switch, `setActivePathwayId` dispatches a `kfe:pathway-changed` document
// event so any mounted screen (the home squiggle) can re-render against the new
// pathway without a navigation cycle.

import { setSetting } from "../data/learner-store.js";

const STORAGE_KEY = "kfe_pathway_id";

/** Default when the learner has never chosen — the canonical learning path. */
export const DEFAULT_PATHWAY_ID = "kfe_zero_to_fluency";

/** @type {string | null} Module-level cache; read once, kept in sync by the setter. */
let _active = null;

/**
 * The learner's active pathway id. Reads localStorage on first call and caches
 * it; falls back to {@link DEFAULT_PATHWAY_ID} when unset or when localStorage
 * is unavailable (private mode / sandboxed contexts).
 *
 * @returns {string}
 */
export function getActivePathwayId() {
  if (_active) return _active;
  try {
    _active = localStorage.getItem(STORAGE_KEY) || DEFAULT_PATHWAY_ID;
  } catch {
    _active = DEFAULT_PATHWAY_ID;
  }
  return _active;
}

/**
 * Persist the learner's chosen pathway and announce the change.
 *   1. Update the in-memory cache + localStorage (canonical, synchronous).
 *   2. Mirror to the learner-store `active_pathway` setting (best-effort, async)
 *      so the existing IndexedDB readers stay correct.
 *   3. Dispatch `kfe:pathway-changed` so mounted screens re-render.
 *
 * Idempotent on value, but always re-dispatches so a caller can force a refresh.
 *
 * @param {string} id  a pathway id, e.g. "kfe_business_english"
 * @returns {void}
 */
export function setActivePathwayId(id) {
  _active = id;
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // localStorage may be disabled; the cache + mirror still carry the value
    // for this session.
  }
  // Mirror to IndexedDB for the async readers. Fire-and-forget: the UI must not
  // block on it, and a failed write only means a reload re-reads the (correct)
  // localStorage value, which getActivePathwayId already prefers.
  void Promise.resolve(setSetting("active_pathway", id)).catch(() => {});
  document.dispatchEvent(new CustomEvent("kfe:pathway-changed", { detail: { id } }));
}

/**
 * Test-only: clear the in-memory cache so the next getActivePathwayId re-reads
 * localStorage. No-op in production.
 */
export function _resetActivePathwayCache() {
  _active = null;
}
